import React, { useCallback, useState } from "react";

import axios from "axios";

import * as Yup from "yup";

import Cancel from "components/Shared/icons/cancel";
import DGAInput from "components/Shared/DGA-inputs/DGA-input";
import { DGACheckboxInput } from "components/Shared/dga-checkbox-input/DGACheckboxInput";
import { RichText } from "@sitecore-jss/sitecore-jss-nextjs";
import ReCAPTCHA from "react-google-recaptcha";
import { DGAButton } from "components/Shared/dga-button/DGAButton";

const allowedKeys = ["Backspace", "ArrowLeft", "ArrowRight", "Delete", "Tab"];

interface SubscribeToNewsLetterFormProps {
  locale: string;
  isSubscribeFormLoading: boolean;
  NewsLetterFormData: any;
  subscribeFormResponseMessage: string;
  setSubscribeFormResponse: React.Dispatch<
    React.SetStateAction<{ success: boolean; message: string }>
  >;
  setPopupOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setIsSubscribeFormLoading: React.Dispatch<React.SetStateAction<boolean>>;
}

const SubscribeToNewsLetterForm = ({
  locale,
  isSubscribeFormLoading,
  NewsLetterFormData,
  setSubscribeFormResponse,
  setPopupOpen,
  setIsSubscribeFormLoading,
}: SubscribeToNewsLetterFormProps) => {
  const [formFieldsPatternError, setFormFieldsPatternError] = React.useState({
    name: "",
    email: "",
    phononumber: "",
  });
  const [termsAndConditionsCheckbox, setTermsAndConditionsCheckbox] = useState<boolean>(false);
  const [marketingEmailsCheckbox, setMarketingEmailsCheckbox] = useState<boolean>(false);
  const [captcha, setCaptcha] = useState<string | null>(null);
  const [captchaError, setCaptchaError] = useState<boolean>(false);
  const [formValues, setFormValues] = useState({
    name: "",
    email: "",
  });
  const sitekey: string = process.env.NEXT_PUBLIC_GOOGLE_CAPTCHA_API_KEY || "";

  const validateEmail = useCallback(
    async (email: string) => {
      const schema = Yup.object().shape({
        email: Yup.string()
          .email(NewsLetterFormData?.fields?.EmailPatternErrorText?.value)
          .required(NewsLetterFormData?.fields?.GlobalRquiredFieldErrorText?.value),
      });

      try {
        await schema.validate({ email }, { abortEarly: false });

        if (formFieldsPatternError?.email) {
          setFormFieldsPatternError((prev) => ({
            ...prev,
            email: "",
          }));
        }

        return true;
      } catch (error: any) {
        if (formFieldsPatternError?.email !== error?.message) {
          setFormFieldsPatternError((prev) => ({
            ...prev,
            email: error?.message,
          }));
        }
        return false;
      }
    },
    [
      formFieldsPatternError?.email,
      NewsLetterFormData?.fields?.EmailPatternErrorText?.value,
      NewsLetterFormData?.fields?.GlobalRquiredFieldErrorText?.value,
    ]
  );

  const handleNameBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      if (!e.target.value && !formFieldsPatternError?.name) {
        setFormFieldsPatternError((prev) => ({
          ...prev,
          name: NewsLetterFormData?.fields?.GlobalRquiredFieldErrorText?.value,
        }));
      }

      if (e.target.value && formFieldsPatternError?.name) {
        setFormFieldsPatternError((prev) => ({
          ...prev,
          name: "",
        }));
      }
    },
    [
      formFieldsPatternError?.name,
      NewsLetterFormData?.fields?.GlobalRquiredFieldErrorText?.value,
      setFormFieldsPatternError,
    ]
  );

  const handleNameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setFormValues((prev) => ({ ...prev, name: e.target.value }));
      if (formFieldsPatternError?.name && e.target.value) {
        setFormFieldsPatternError((prev) => ({
          ...prev,
          name: "",
        }));
      } else if (!formFieldsPatternError?.name && !e.target.value) {
        setFormFieldsPatternError((prev) => ({
          ...prev,
          name: NewsLetterFormData?.fields?.GlobalRquiredFieldErrorText?.value,
        }));
      }
    },
    [
      formFieldsPatternError?.name,
      NewsLetterFormData?.fields?.GlobalRquiredFieldErrorText?.value,
      setFormFieldsPatternError,
    ]
  );

  const handleEmailBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      validateEmail(e?.target?.value);
    },
    [validateEmail]
  );

  const handleEmailChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setFormValues((prev) => ({ ...prev, email: e.target.value }));
      if (formFieldsPatternError?.email) {
        validateEmail(e?.target?.value);
      }
    },
    [formFieldsPatternError?.email, validateEmail]
  );

  const handlePhoneNumberFieldKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (!/[0-9]/.test(e.key) && !allowedKeys.includes(e.key)) {
        e.preventDefault();
      }
    },
    [allowedKeys]
  );

  const handleSubscribeToNewsLetter = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!captcha) {
      setCaptchaError(true);
      return;
    }

    setCaptchaError(false);
    setIsSubscribeFormLoading(true);
    setSubscribeFormResponse({
      success: false,
      message: "",
    });

    try {
      const formData = new FormData(e.currentTarget);

      const Name = formData.get("name") as string;
      const Email = formData.get("email") as string;
      const PhoneNumber = formData.get("phononumber") as string;
      //   const MarketingEmails = formData.get("marketingEmails") as string;
      //   const TermsAndConditions = formData.get("termsAndConditions") as string;

      await validateEmail(Email);

      const response = await axios.post(
        `${process.env.NEXT_PUBLIC_SITECORE_REST}/api/Subscription/moc/Submit`,
        {
          Language: locale,
          Name,
          Email,
          PhoneNumber,
          MarketingEmails: marketingEmailsCheckbox,
          TermsAndConditions: termsAndConditionsCheckbox,
          CaptchaResponse: captcha,
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      setSubscribeFormResponse({
        success: true,
        message: response?.data,
      });

      setIsSubscribeFormLoading(false);
      setCaptcha(null); // Reset captcha after successful submission
      setCaptchaError(false); // Reset captcha error
      setFormValues({ name: "", email: "" }); // Reset form values after successful submission
      setTermsAndConditionsCheckbox(false); // Reset checkboxes
      setMarketingEmailsCheckbox(false);
    } catch (error: any) {
      console.error(error?.response?.data);

      setSubscribeFormResponse({
        success: false,
        message: error?.response?.data || "Something went wrong!",
      });

      setIsSubscribeFormLoading(false);
    }
  };

  const handleCaptchaChange = (value: string | null) => {
    setCaptcha(value);
    // Clear captcha error when user completes it
    if (value) {
      setCaptchaError(false);
    }
  };

  // Check if submit button should be disabled
  const isSubmitDisabled = 
    isSubscribeFormLoading ||
    !captcha ||
    !formValues.name.trim() ||
    !formValues.email.trim() ||
    !termsAndConditionsCheckbox;

  return (
    <div className="flex flex-col rounded-lg items-start gap-5 p-5 bg-white max-w-sm w-full z-20 animate-appearance-in max-h-[90vh] overflow-y-auto">
      {/* close popup button */}
      <button
        type="button"
        onClick={() => setPopupOpen(false)}
        aria-label="Close newsletter dialog"
        className="flex items-center justify-center w-fit self-end p-1 hover:!bg-[#edebea] transition-colors rounded sticky top-0 bg-white z-10"
      >
        <Cancel color="#161616" aria-hidden={true} />
      </button>

      {/* form */}
      <form
        onSubmit={handleSubscribeToNewsLetter}
        className="flex flex-col items-start gap-5 w-full"
      >
        {/* name field */}
        {(NewsLetterFormData?.fields?.NameLabel?.value ||
          NewsLetterFormData?.fields?.NamePlaceholder?.value) && (
          <DGAInput
            label={NewsLetterFormData?.fields?.NameLabel?.value}
            id="name"
            name="name"
            type="text"
            required
            placeholder={NewsLetterFormData?.fields?.NamePlaceholder?.value}
            onBlur={handleNameBlur}
            onChange={handleNameChange}
            error={formFieldsPatternError?.name?.length > 0}
            errorMessage={formFieldsPatternError?.name}
            InputSize={"lg"}
            variant="Filled-Darker"
          />
        )}

        {/* email field */}
        {(NewsLetterFormData?.fields?.EmailLabel?.value ||
          NewsLetterFormData?.fields?.EmailPlaceholder?.value) && (
          <DGAInput
            label={NewsLetterFormData?.fields?.EmailLabel?.value}
            name="email"
            type="email"
            required
            placeholder={NewsLetterFormData?.fields?.EmailPlaceholder?.value}
            onChange={handleEmailChange}
            onBlur={handleEmailBlur}
            error={formFieldsPatternError?.email?.length > 0}
            errorMessage={formFieldsPatternError?.email}
            InputSize={"lg"}
            variant="Filled-Darker"
          />
        )}

        {/* phone number field */}
        {(NewsLetterFormData?.fields?.PhoneNumberLabel?.value ||
          NewsLetterFormData?.fields?.PhoneNumberPlaceholder?.value) && (
          <DGAInput
            label={
              NewsLetterFormData?.fields?.PhoneNumberLabel?.value +
              " " +
              NewsLetterFormData?.fields?.OptionalInputLabelSuffix?.value
            }
            id="phononumber"
            name="phononumber"
            type="phononumber"
            error={formFieldsPatternError?.phononumber?.length > 0}
            errorMessage={formFieldsPatternError?.phononumber}
            InputSize={"lg"}
            onKeyDown={handlePhoneNumberFieldKeyDown}
            maxLength={25}
            placeholder={NewsLetterFormData?.fields?.PhoneNumberPlaceholder?.value}
            variant="Filled-Darker"
          />
        )}

        {/* checkboxes */}

        {/* terms and conditions checkbox */}
        {NewsLetterFormData?.fields?.TermsAndConditionsLabel?.value && (
          <div className="flex items-center gap-2">
            <DGACheckboxInput
              size="md"
              id={"termsAndConditions"}
              checked={termsAndConditionsCheckbox}
              variant="primary"
              onChange={() => {
                setTermsAndConditionsCheckbox((prev) => !prev);
              }}
            />
            <label htmlFor="termsAndConditions" className="text-primary-1000">
              <RichText field={NewsLetterFormData?.fields?.TermsAndConditionsLabel} />
            </label>
          </div>
        )}

        {/* marketing emails checkbox */}
        {NewsLetterFormData?.fields?.MarketingEmailsLabel?.value && (
          <div className="flex items-center gap-2">
            <DGACheckboxInput
              size="md"
              id={"marketingEmails"}
              checked={marketingEmailsCheckbox}
              variant="primary"
              onChange={() => {
                setMarketingEmailsCheckbox((prev) => !prev);
              }}
            />
            <label htmlFor="marketingEmails" className="text-primary-1000">
              <RichText field={NewsLetterFormData?.fields?.MarketingEmailsLabel} />
            </label>
          </div>
        )}

        {/* reCAPTCHA */}
        <div className={`flex items-center ${captchaError ? "border-2 border-red-500 rounded p-1" : ""}`}>
          <ReCAPTCHA
            sitekey={sitekey}
            onChange={handleCaptchaChange}
            hl={locale}
          />
        </div>

        {/* submit button */}
        {NewsLetterFormData?.fields?.SubmitButtonText?.value && (
          <DGAButton
            type="submit"
            disabled={isSubmitDisabled}
            size="lg"
            variant="primary"
            buttonContent={NewsLetterFormData?.fields?.SubmitButtonText?.value}
            className="min-w-[85px] w-fit min-h-[42px] whitespace-nowrap"
          />
        )}
      </form>
    </div>
  );
};

export default React.memo(SubscribeToNewsLetterForm);
